import { calcCompanyActiveYears, getCurrentYear } from "~/helpers/date";
import { findCheapestProduct } from "~/helpers/product";
import type { CountryKey, Faq, Product, ProductName } from "~/types";

type LocaleSpecificFaqs = Partial<Record<CountryKey, Faq[]>>;
interface Options { hasGlobals?: boolean; }

export default function useFaq(country: CountryKey, products: Product[], options: Options = {}) {
  const { tm, rt } = useI18n();
  const domain = useNuxtApp().$domain;
  const { displayPrice } = useFormat();
  const { reviewLinksHtml } = useSocials();
  const { whatsapp, phone, supportEmail } = useCommunication();

  const faqs = getFaqMessages(`${country}_page.faq`);
  faqs.push(...getLocaleSpecificFaqs());
  if (options.hasGlobals) {
    faqs.push(...getFaqMessages('global_faqs'));
  }

  return {
    faqs
  };

  function getFaqMessages(key: string) {
    const messages = tm(key);
    const answerArgs = buildAnswerArgs(products);
  if (Array.isArray(messages)) {
      return messages.map(faq => buildFaq(faq, answerArgs));
    }
    return [];
  }

  function buildFaq(faq: Faq, answerArgs: Record<string, any>): Faq {
    return {
      q: rt(faq.q, { domain }),
      a: rt(faq.a, answerArgs)
    };
  }

  function buildProductToPriceMap(products: Product[]) {
    return products.reduce((acc, p) => {
      acc[`${p.name}_price`] = displayPrice(p.price);
      return acc;
    }, {} as Record<`${ProductName}_price`, string>)
  }

  function getLocaleSpecificFaqs(): Faq[] {
    const localeSpecificFaqs = tm('locale_specific_faq') as LocaleSpecificFaqs;
    const answerArgs = buildAnswerArgs(products);
    return (localeSpecificFaqs?.[country] || []).map(faq => buildFaq(faq, answerArgs));
  }

  function getStartingPrice(products: Product[]) {
    const { price } = findCheapestProduct(products);
    return displayPrice(price);
  }

  function buildAnswerArgs(products: Product[]) {
    return {
      ...buildProductToPriceMap(products),
      price: getStartingPrice(products),
      currentYear: getCurrentYear(),
      yearsActive: calcCompanyActiveYears(),
      socialLinks: reviewLinksHtml,
      email: supportEmail,
      phone: phone.raw,
      whatsapp, domain
    }
  }
}